import { toast } from 'react-toastify';
import { isAxiosError } from 'axios';

type ApiErrorBody = {
  detail?: unknown;
  message?: unknown;
  error?: unknown;
  errors?: unknown;
  non_field_errors?: unknown;
};

const SKIP_BODY_KEYS = new Set([
  'success',
  'status',
  'code',
  'error_code',
  'trace_id',
  'request_id',
]);

const STATUS_MESSAGES: Record<number, string> = {
  400: 'Please check the information you entered and try again.',
  401: 'Your session has expired. Please sign in again.',
  403: 'You do not have permission to do that.',
  404: 'We could not find what you were looking for. It may have been removed.',
  409: 'That action conflicts with something already on your account. Please refresh and try again.',
  413: 'That file is too large. Please choose a smaller file and try again.',
  422: 'Please check the information you entered and try again.',
  429: 'Too many attempts. Please wait a moment and try again.',
  500: 'Something went wrong on our side. Please try again in a moment.',
  502: 'Our servers are briefly unavailable. Please try again shortly.',
  503: 'Our servers are briefly unavailable. Please try again shortly.',
  504: 'That request took too long. Please try again.',
};

const NETWORK_MESSAGE =
  'We could not reach the server. Check your internet connection and try again.';

const FRIENDLY_MESSAGE_OVERRIDES: Array<{ test: RegExp; message: string }> = [
  {
    test: /maximum of 6 prompts|max(imum)? of 6 prompts/i,
    message:
      'You already have 6 prompts saved — that’s the limit. Edit an existing prompt or remove one before adding another.',
  },
  {
    test: /content.*required|this field is required/i,
    message: 'Each prompt needs question text before it can be saved.',
  },
  {
    test: /video.*required|file.*required/i,
    message: 'Please add a video response before saving this prompt.',
  },
];

function polishUserMessage(message: string): string {
  const trimmed = message.trim();
  if (!trimmed) return trimmed;

  for (const { test, message: friendly } of FRIENDLY_MESSAGE_OVERRIDES) {
    if (test.test(trimmed)) {
      return friendly;
    }
  }

  return trimmed;
}

function collectMessageParts(value: unknown): string[] {
  if (value == null) {
    return [];
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  if (Array.isArray(value)) {
    return value.flatMap(collectMessageParts);
  }
  if (typeof value === 'object') {
    const row = value as Record<string, unknown>;
    if (typeof row.detail === 'string' || Array.isArray(row.detail)) {
      return collectMessageParts(row.detail);
    }
    if (typeof row.message === 'string' || Array.isArray(row.message)) {
      return collectMessageParts(row.message);
    }
    return Object.values(row).flatMap(collectMessageParts);
  }
  return [];
}

function joinMessages(parts: string[]): string | null {
  const unique = Array.from(new Set(parts.map((part) => part.trim()).filter(Boolean)));
  return unique.length ? unique.join(' ') : null;
}

function extractServerMessage(data: unknown): string | null {
  if (typeof data === 'string') {
    const trimmed = data.trim();
    return trimmed || null;
  }
  if (!data || typeof data !== 'object') {
    return null;
  }

  const body = data as ApiErrorBody & Record<string, unknown>;
  const priorityKeys = ['detail', 'message', 'error', 'non_field_errors', 'errors'];

  for (const key of priorityKeys) {
    if (!(key in body)) {
      continue;
    }
    const message = joinMessages(collectMessageParts(body[key]));
    if (message) {
      return message;
    }
  }

  const fieldMessages: string[] = [];
  for (const [key, value] of Object.entries(body)) {
    if (SKIP_BODY_KEYS.has(key) || priorityKeys.includes(key)) {
      continue;
    }
    fieldMessages.push(...collectMessageParts(value));
  }

  return joinMessages(fieldMessages);
}

/**
 * Turn API, network, or thrown errors into short copy suitable for toasts and inline alerts.
 */
export function getUserFacingError(
  err: unknown,
  fallback = 'Something went wrong. Please try again.'
): string {
  if (typeof err === 'string' && err.trim()) return err.trim();

  if (isAxiosError(err)) {
    if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) {
      return 'That request took too long. Please try again.';
    }
    if (!err.response) return NETWORK_MESSAGE;

    const fromBody = extractServerMessage(err.response.data);
    if (fromBody) return polishUserMessage(fromBody);

    const statusMsg = STATUS_MESSAGES[err.response.status];
    if (statusMsg) return statusMsg;
  }

  if (err && typeof err === 'object' && !(err instanceof Error)) {
    const fromBody = extractServerMessage(err);
    if (fromBody) return polishUserMessage(fromBody);
  }

  if (err instanceof Error && err.message.trim()) {
    const msg = err.message.trim();
    if (/network error|failed to fetch/i.test(msg)) return NETWORK_MESSAGE;
    if (!/^request failed with status code/i.test(msg)) return msg;
  }

  return fallback;
}

const TOAST_DEDUP_MS = 8000;
let lastToastMessage = '';
let lastToastAt = 0;

/** Show a friendly toast for a failed user action (deduped within a short window). */
export function notifyActionError(err: unknown, fallback?: string): string {
  const message = getUserFacingError(err, fallback);
  const now = Date.now();
  if (message === lastToastMessage && now - lastToastAt < TOAST_DEDUP_MS) {
    return message;
  }
  lastToastMessage = message;
  lastToastAt = now;
  toast.error(message);
  return message;
}

/** True when the server is unreachable or temporarily unavailable (safe to use cached data). */
export function isRecoverableFetchError(err: unknown): boolean {
  if (isAxiosError(err)) {
    if (!err.response) return true;
    const status = err.response.status;
    return status === 502 || status === 503 || status >= 500;
  }
  if (err instanceof Error) {
    return /network error|failed to fetch|load failed/i.test(err.message);
  }
  return false;
}

export function isAuthFetchError(err: unknown): boolean {
  if (isAxiosError(err)) {
    const status = err.response?.status;
    return status === 401 || status === 403;
  }
  return false;
}
